import { NativeBridgeResponse } from 'scbk-services/types/bridge/global';
import { NavbarType } from 'scbk-services/types/menu/menu';
import { useNativeBridgeCallbackRegistryStore } from '../stores/nativeBridgeCallbackRegistry';
import { useNativeBridge } from './useNativeBridge';

export namespace Webview {
    export interface DoNOpenWebViewRequest {
        openURL: string;
        callbackFunction?: () => void;
        title: string;
    }

    /**
     *
     */
    export interface DoNOpenBizWebViewRequest {
        type: string;
        url: string;
        closeCurrentWebview?: boolean;
        method?: 'get' | 'post' | 'GET' | 'POST'; //method
        data?: string | undefined | null; //post data
    }

    /**
     *
     */
    export interface DoNCloseBizWebViewRequest {
        type: string;
        navbar?: {
            visible: boolean;
        };
    }

    export interface DoNGetCurrentWebviewStatusResponse {
        isTop: boolean;
        //isVisible: boolean;
    }

    export interface DoNCheckNavbarPermissionRequest {
        id?: NavbarType;
        url?: string;
    }
}

/**
 * 웹뷰 네이티브 브릿지 hook
 * @returns
 */
export const useWebviewBridge = () => {
    const nativeBridge = useNativeBridge();
    const { registerCallback } = useNativeBridgeCallbackRegistryStore.getState();

    /**
     * 임베디드 웹뷰 열기
     * @param request
     */
    const doNOpenWebView = async (request: Webview.DoNOpenWebViewRequest) => {
        nativeBridge('doNOpenWebView', request, { onlyRequest: true });
    };

    /**
     * 임베디드 웹뷰 닫기
     * @param mydataCode
     */
    const doNCloseWebView = async (mydataCode: string) => {
        nativeBridge('doNCloseWebView', { mydataCode }, { onlyRequest: true });
    };

    /**
     * 웹뷰 내리기
     */
    const doNWebViewDown = async () => {
        nativeBridge('doNWebViewDown', {}, { onlyRequest: true });
    };

    /**
     * 웹뷰 올리기
     */
    const doNWebViewUp = async () => {
        nativeBridge('doNWebViewUp', {}, { onlyRequest: true });
    };

    /**
     * 외부브라우져(사라피, 크롬)열기
     * @param url
     */
    const doNWebBrowser = async (url: string) => {
        await nativeBridge('doNWebBrowser', { url }, { onlyRequest: true });
    };

    /**
     * 외부브라우져(사라피, 크롬)열기 WEB VIEW 백 버튼
     * @param url
     */
    const doNNewWebView = async (url: string) => {
        await nativeBridge('doNNewWebView', { openURL: url }, { onlyRequest: true });
    };

    /**
     * 업무 웹뷰 open
     */
    const doNOpenBizWebView = async (request: Webview.DoNOpenBizWebViewRequest) => {
        const defaultRequest: Webview.DoNOpenBizWebViewRequest = {
            type: '',
            url: '',
            closeCurrentWebview: false,
            method: 'GET',
        };

        const mergedRequest = {
            ...defaultRequest,
            ...request,
        };

        console.log('# doNOpenBizWebView request=', mergedRequest);
        nativeBridge('doNOpenBizWebView', mergedRequest, { onlyRequest: true });
    };

    /**
     * 업무 웹뷰 close
     * @param request
     */
    const doNCloseBizWebView = async (request: Webview.DoNCloseBizWebViewRequest) => {
        const defaultRequest: Webview.DoNCloseBizWebViewRequest = {
            type: '',
            navbar: {
                visible: false,
            },
        };

        const mergedRequest = {
            ...defaultRequest,
            ...request,
        };

        console.log('# doNCloseBizWebView request=', mergedRequest);

        nativeBridge('doNCloseBizWebView', mergedRequest, { onlyRequest: true });
    };

    /**
     *
     */
    const doNShowMainNavBar = async () => {
        nativeBridge('doNShowMainNavBar', {}, { onlyRequest: true });
    };

    /**
     *
     */
    const doNHideMainNavBar = async () => {
        nativeBridge('doNHideMainNavBar', {}, { onlyRequest: true });
    };

    /**
     * 메인웹뷰 탭 이동
     * @param tabType 'HOME' | 'TRANSFER' | 'INQUIRY' | 'INVEST' | 'PRODUCT' | 'ALL' | PRE-LOGIN | ''
     */
    const doNGoMain = async (
        tabType:
            | 'HOME'
            | 'TRANSFER'
            | 'INQUIRY'
            | 'INVEST'
            | 'PRODUCT'
            | 'ALL'
            | 'PRE-LOGIN'
            | '' = '',
    ) => {
        nativeBridge('doNGoMain', { tabType }, { onlyRequest: true });
    };

    /**
     * 웹뷰 페이지 로드 완료
     */
    const doNPageLoadCompleted = async () => {
        nativeBridge('doNPageLoadCompleted', {}, { onlyRequest: true });
    };

    /**
     * 웹뷰 초기화 처리
     * 메인웹뷰/업무웹뷰 초기화
     */
    const doNInitWebview = async () => {
        nativeBridge('doNInitWebview', {}, { onlyRequest: true });
    };

    /**
     * 웹뷰 하단 네비바 상태정보 조회
     * @returns
     */
    const doNGetMainNavBarStatus = async () => {
        const res = await nativeBridge<{}, { result: string }>('doNGetMainNavBarStatus', {});

        const { status, payload } = res;

        if (status === 'fail') {
            return;
        }

        if (!payload) {
            return;
        }

        return payload.result;
    };

    /**
     * 현재웹뷰 상태정보를 조회한다.
     * @returns
     */
    const doNGetCurrentWebviewStatus = async () => {
        const res = await nativeBridge<{}, Webview.DoNGetCurrentWebviewStatusResponse>(
            'doNGetCurrentWebviewStatus',
            {},
        );

        const { status, payload } = res;

        if (status === 'fail') {
            return {
                isTop: false,
                isVisible: false,
            } as Webview.DoNGetCurrentWebviewStatusResponse;
        }

        if (!payload) {
            return {
                isTop: false,
                isVisible: false,
            } as Webview.DoNGetCurrentWebviewStatusResponse;
        }

        return payload as Webview.DoNGetCurrentWebviewStatusResponse;
    };

    /**
     *
     * @param callback
     */
    const doNCheckNavbarPermission = (
        callback?: (request: Webview.DoNCheckNavbarPermissionRequest) => void | Promise<void>,
    ) => {
        registerCallback('doNCheckNavbarPermission', {
            once: false,
            fn: (res: NativeBridgeResponse<Webview.DoNCheckNavbarPermissionRequest>) => {
                const { payload, status } = res;

                if (status === 'fail') {
                    return;
                }

                if (!payload) {
                    return;
                }

                callback && callback(payload);
            },
        });
    };

    /**
     * 네비바 메뉴 접근제어 처리 결과값을 네이티브에 전달한다.
     * @param result 네비바 메뉴 접근제어 처리 결과값
     */
    const doNCheckNavbarPermissionResult = async (result: 'Y' | 'N' = 'Y') => {
        nativeBridge<{ result: 'Y' | 'N' }, {}>(
            'doNCheckNavbarPermissionResult',
            {
                result,
            },
            { onlyRequest: true },
        );
    };

    /**
     * 다크모드 변경시 native 감지용 bridge
     */
    const doNUpdateNavBar = async () => {
        nativeBridge('doNUpdateNavBar', {}, { onlyRequest: true });
    };

    /**
     * 하단 네비바 선택 clear
     */
    const doNClearNavibarSelection = async () => {
        nativeBridge('doNClearNavibarSelection', {}, { onlyRequest: true });
    };

    /**
     * 로그인 후 메인웹뷰 (백그라운드에서) 메인이동 처리
     */
    const doNLoginCompleted = async () => {
        nativeBridge('doNLoginCompleted', {}, { onlyRequest: true });
    };

    /**
     * 웹뷰간 이벤트 전달 브로드캐스트
     * @param eventName
     * @param data
     */
    const doNBroadCast = async (event: string, data?: unknown) => {
        nativeBridge<{ event: string; data?: unknown }, {}>(
            'doNBroadCast',
            { event, data: encodeURIComponent(JSON.stringify(data)) },
            { onlyRequest: true },
        );
    };

    return {
        doNOpenWebView,
        doNCloseWebView,
        doNWebViewDown,
        doNWebViewUp,
        doNOpenBizWebView,
        doNCloseBizWebView,
        doNShowMainNavBar,
        doNHideMainNavBar,
        doNGoMain,
        doNWebBrowser,
        doNNewWebView,
        doNPageLoadCompleted,
        doNInitWebview,
        doNGetMainNavBarStatus,
        doNGetCurrentWebviewStatus,
        doNCheckNavbarPermission,
        doNCheckNavbarPermissionResult,
        doNUpdateNavBar,
        doNClearNavibarSelection,
        doNLoginCompleted,
        doNBroadCast,
    };
};
